import React, { useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import GetAppIcon from '@mui/icons-material/GetApp'

import { requests } from '../../../core/ajax'
import { feedback } from '../../../core/feedback'
import { TextInput, ThemeButton, ViewPDFDialog, FoldingPanel, FileUploaderInput } from '../../../ui'
import { ModalContext } from '../../../ui/modal/contexts'
import { useCombos } from '../../combos'
import { useCompanyProfileContext } from '../../company-profile/contexts'
import { COMPANY_FORM_CONFIG } from '../../company-profile/constants'
import {
  ACCEPTED_APPS,
  PENDING_APPS,
  OUTRANGED_APPS,
  REJECTED_APPS,
} from '../../company-profile/components/public-offer/constants/status-po.constants'
import { ORIGEN_KEYS_PRESCRIPTOR } from '../../company-profile/components/public-offer/constants'
import { assembleInstallerSecondaryPutPayload } from '../../company-profile/logics'
import { downloadFile } from '../../../core/utils'
import { getEnv } from '../../../config/env'

const REACT_APP_WP_CONTENT_CREATOR_URL = getEnv('REACT_APP_WP_CONTENT_CREATOR_URL')

const openPdfInModal = ({ open, fileId, onRead, docId }) => {
  requests
    .getById('/documento', fileId)
    .then(({ data }) => {
      const base64Data = data['documento'].split("'")[1]
      const file = `data:application/pdf;base64,${base64Data}`
      if (docId !== undefined) {
        open({
          Component: ViewPDFDialog,
          data: { file, docId, setMultipleArray: onRead },
          type: 'fullScreen',
          closable: false,
        })
      } else {
        open({
          Component: ViewPDFDialog,
          data: { file, setDisabled: onRead, shortcutEnabled: true },
          type: 'fullScreen',
        })
      }
    })
    .catch(() => feedback('error', 'No se encuentra el documento'))
}

const downloadPdf = (fileId) => {
  requests
    .getById('/documento', fileId)
    .then(({ data }) => downloadFile(data['documento'], data['extension'], data['nombre']))
    .catch(() => feedback('error', 'No se encuentra el documento'))
}

const buildFilialPutPayload = (original, formData, generalConditions) => ({
  nombre: original.nombre,
  razon_social: original.razon_social,
  telefono: original.telefono || '',
  email: original.email || '',
  acepta_uso_marca: original.acepta_uso_marca === '01',
  acepta_uso_marca_checkbox: original.acepta_uso_marca_checkbox,
  codigo_registro_industrial: original.codigo_registro_industrial,
  responsable_legal: formData.responsable_legal || '',
  nif_responsable_legal: formData.nif_responsable_legal || '',
  calle: original.calle || '',
  numero: original.numero || '',
  piso: original.piso || null,
  poligono: original.poligono || null,
  poblacion: original.poblacion || '',
  cod_codigo_postal: original.cod_codigo_postal || '',
  check_resp_sec_info: original.check_resp_sec_info === true,
  acepta_condiciones_generales_op: generalConditions,
  ...(formData.iban_op ? { iban_op: formData.iban_op.replace(/\s/g, '') } : {}),
})

const CampaignList = ({ campaignsData, selectedIds, onToggle, docsReaded, generalConditions }) => {
  const { formatMessage } = useIntl()
  const {
    actions: { open },
  } = useContext(ModalContext)
  if (!campaignsData) return null

  const allCampaigns = (campaignsData.listado_campanas || []).filter(
    (c) => !OUTRANGED_APPS[c.estado] && ORIGEN_KEYS_PRESCRIPTOR.includes(c.origen)
  )

  if (allCampaigns.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        {formatMessage({ id: 'pages.adhesionOp.noCampaigns' })}
      </Typography>
    )
  }

  return (
    <Box>
      <FoldingPanel
        title={formatMessage({ id: 'pages.adhesionOp.campaignsPrescriptors' })}
        isExpanded
      >
        {allCampaigns.map((po) => {
          const isAccepted = ACCEPTED_APPS[po.estado]
          const isPending = PENDING_APPS[po.estado]
          const isRejected = REJECTED_APPS[po.estado]
          const docRead = docsReaded.includes(po.id) || isAccepted || isPending
          const docId = po.d_condiciones_economicas?.id

          return (
            <Box
              key={po.id}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={1}
            >
              <Typography variant="body2" style={{ flex: 1 }}>
                {po.nombre}
                {isAccepted && (
                  <Chip
                    size="small"
                    label={formatMessage({ id: 'pages.adhesionOp.accepted' })}
                    color="success"
                    style={{ marginLeft: 8 }}
                  />
                )}
                {isPending && (
                  <Chip
                    size="small"
                    label={formatMessage({ id: 'pages.adhesionOp.pending' })}
                    color="warning"
                    style={{ marginLeft: 8 }}
                  />
                )}
                {isRejected && (
                  <Chip
                    size="small"
                    label={formatMessage({ id: 'pages.adhesionOp.rejected' })}
                    color="error"
                    style={{ marginLeft: 8 }}
                  />
                )}
              </Typography>
              <Box display="flex" alignItems="center">
                {docId && (
                  <>
                    <PictureAsPdfIcon
                      style={{
                        color: docRead ? '#F6BF00' : '#004a6b',
                        cursor: 'pointer',
                        marginRight: 4,
                      }}
                      onClick={() =>
                        openPdfInModal({
                          open,
                          fileId: docId,
                          docId: po.id,
                          onRead: (id) => onToggle('docRead', id),
                        })
                      }
                    />
                    <GetAppIcon
                      style={{ color: '#004a6b', cursor: 'pointer', marginRight: 4 }}
                      onClick={() => downloadPdf(docId)}
                    />
                  </>
                )}
                <Typography variant="caption" style={{ marginRight: 4 }}>
                  {formatMessage({ id: 'global.accept' })}
                </Typography>
                <span
                  style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => {
                    if (isAccepted || isPending) return
                    if (isRejected) {
                      feedback('warning', 'Esta oferta ha sido rechazada y no puede seleccionarse')
                    } else if (!generalConditions) {
                      feedback('warning', 'Debes aceptar las condiciones generales primero')
                    } else if (!docRead && docId) {
                      feedback(
                        'warning',
                        'Debes leer el documento de la oferta antes de seleccionarla'
                      )
                    }
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={isAccepted || isPending || selectedIds.includes(po.id)}
                    disabled={
                      isAccepted ||
                      isPending ||
                      isRejected ||
                      !generalConditions ||
                      (!docRead && !!docId)
                    }
                    onChange={() => onToggle('campaign', po.id)}
                  />
                </span>
              </Box>
            </Box>
          )
        })}
      </FoldingPanel>
    </Box>
  )
}

const RGPD_MISSING_MESSAGE =
  'Para continuar, completa primero el cuestionario RGPD de la empresa principal'

const FilialStep = ({ filial, combos, onSaveAndContinue, onPrevious, isLast, primaryHasRgpd }) => {
  const intl = useIntl()
  const {
    actions: { open },
  } = useContext(ModalContext)
  const [formData, setFormData] = useState({
    responsable_legal: filial.responsable_legal || '',
    nif_responsable_legal: filial.nif_responsable_legal || '',
    iban_op: filial.iban_op || '',
  })
  const [campaignsData, setCampaignsData] = useState(null)
  const [loadingCampaigns, setLoadingCampaigns] = useState(true)
  const [selectedCampaignIds, setSelectedCampaignIds] = useState([])
  const [docsReaded, setDocsReaded] = useState([])

  // Inicialización primaria desde el objeto filial
  const [generalConditions, setGeneralConditions] = useState(
    !!filial.acepta_condiciones_generales_op
  )
  const [generalCheckAvailable, setGeneralCheckAvailable] = useState(
    !!filial.acepta_condiciones_generales_op
  )
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    requests
      .get(`/campana/listado_campanas/${filial.id}`)
      .then(({ data }) => {
        setCampaignsData(data)

        const hasAcceptedConditions =
          !!data?.acepta_condiciones_generales_op || !!filial.acepta_condiciones_generales_op

        if (hasAcceptedConditions) {
          setGeneralConditions(true)
          setGeneralCheckAvailable(true)
        }
      })
      .catch(() => feedback('error', 'Error al cargar las campañas de la empresa'))
      .finally(() => setLoadingCampaigns(false))
  }, [filial.id, filial.acepta_condiciones_generales_op])

  const generalConditionsDocumentId = campaignsData?.condiciones_generales_op_id

  const localInputProps = {
    formState: formData,
    onChange: (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value })),
    combos,
    intl,
  }

  const ccaaLabel =
    combos.cod_zona_zeus?.data?.find((z) => z.key === String(filial.comunidad_autonoma))?.value ||
    filial.comunidad_autonoma

  const handleToggle = (type, id) => {
    if (type === 'docRead') {
      setDocsReaded((prev) => (prev.includes(id) ? prev : [...prev, id]))
    } else {
      setSelectedCampaignIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      )
    }
  }

  const handleToggleGeneralConditions = () => {
    if (!generalConditionsDocumentId) {
      feedback('warning', 'No hay documento de condiciones generales disponible')
      return
    }
    if (!generalCheckAvailable) {
      feedback('warning', 'Debes leer las condiciones generales antes de aceptar')
      return
    }
    setGeneralConditions((prev) => !prev)
  }

  const hasFormChanges =
    formData.responsable_legal !== (filial.responsable_legal || '') ||
    formData.nif_responsable_legal !== (filial.nif_responsable_legal || '')
  const hasConditionsChange = generalConditions !== !!filial.acepta_condiciones_generales_op

  const handleSave = async () => {
    if (!primaryHasRgpd) {
      feedback('error', RGPD_MISSING_MESSAGE)
      return
    }
    const needsPut = hasFormChanges || hasConditionsChange
    setSaving(true)
    try {
      if (needsPut) {
        await requests.putById(
          '/empresa_ca',
          filial.id,
          buildFilialPutPayload(filial, formData, generalConditions)
        )
      }
      if (generalConditions && selectedCampaignIds.length > 0) {
        await requests.post('/campana/adherirse', {
          empresa_ca_id: filial.id,
          campanas: selectedCampaignIds,
          acepta_condiciones_generales_op: generalConditions,
        })
      }
      feedback('success', `Empresa ${filial.nombre || filial.razon_social} guardada correctamente`)
      onSaveAndContinue()
    } catch {
      feedback('error', 'Error al guardar los datos de la empresa filial')
    } finally {
      setSaving(false)
    }
  }

  const isAlreadyFilled = !!(filial.responsable_legal && filial.nif_responsable_legal)

  return (
    <Box>
      {isAlreadyFilled && (
        <Box mb={2}>
          <Alert severity="info">
            {intl.formatMessage({ id: 'pages.adhesionOp.dataAlreadyFilled' })}
          </Alert>
        </Box>
      )}
      <Box mb={3} display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <Typography variant="h6" color="primary">
          {filial.nombre || filial.razon_social}
        </Typography>
        {ccaaLabel && (
          <Chip
            size="small"
            label={`Comunidad Autónoma: ${ccaaLabel}`}
            variant="outlined"
            color="primary"
          />
        )}
      </Box>

      <Typography variant="subtitle1" color="primary" gutterBottom>
        {intl.formatMessage({ id: 'pages.adhesionOp.dataForPublicOffer' })}
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextInput {...COMPANY_FORM_CONFIG(localInputProps).RESPONSABLE_LEGAL} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextInput {...COMPANY_FORM_CONFIG(localInputProps).NIF_RESPONSABLE_LEGAL} />
        </Grid>
      </Grid>

      <Box
        mt={3}
        display="flex"
        flexDirection="column"
        border="1px solid"
        borderColor="grey.400"
        sx={{ borderRadius: '3px' }}
        bgcolor="common.white"
      >
        <Box m={4} display="flex">
          <Typography variant="h6" color="primary">
            {intl.formatMessage({ id: 'pages.adhesionOp.generalConditionsAndCampaigns' })}
          </Typography>
        </Box>
        {loadingCampaigns ? (
          <Box display="flex" justifyContent="center" pb={4}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box mx={4} mb={4} display="flex" flexDirection="column">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={2}
              p={1}
              bgcolor="grey.50"
              sx={{ borderRadius: '3px' }}
            >
              <Typography variant="body2">
                {intl.formatMessage({ id: 'pages.adhesionOp.acceptGeneralConditions' })}
              </Typography>
              <Box display="flex" alignItems="center">
                {generalConditionsDocumentId && (
                  <>
                    <PictureAsPdfIcon
                      style={{
                        color:
                          docsReaded.includes(generalConditionsDocumentId) ||
                            generalCheckAvailable ||
                            generalConditions
                            ? '#F6BF00'
                            : '#004a6b',
                        cursor: 'pointer',
                        marginRight: 4,
                      }}
                      onClick={() =>
                        openPdfInModal({
                          open,
                          fileId: generalConditionsDocumentId,
                          onRead: () => setGeneralCheckAvailable(true),
                        })
                      }
                    />
                    <GetAppIcon
                      style={{ color: '#004a6b', cursor: 'pointer', marginRight: 4 }}
                      onClick={() => downloadPdf(generalConditionsDocumentId)}
                    />
                  </>
                )}
                <Typography variant="caption" style={{ marginRight: 4 }}>
                  {intl.formatMessage({ id: 'pages.adhesionOp.accept' })}
                </Typography>
                <span
                  style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => {
                    if (!generalConditionsDocumentId) {
                      feedback('warning', 'No hay documento de condiciones generales disponible')
                    } else if (!generalCheckAvailable) {
                      feedback('warning', 'Debes leer las condiciones generales antes de aceptar')
                    }
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={generalConditions}
                    disabled={!generalConditionsDocumentId || !generalCheckAvailable}
                    onChange={handleToggleGeneralConditions}
                    color="primary"
                  />
                </span>
              </Box>
            </Box>

            <CampaignList
              campaignsData={campaignsData}
              selectedIds={selectedCampaignIds}
              onToggle={handleToggle}
              docsReaded={docsReaded}
              generalConditions={generalConditions}
            />
          </Box>
        )}
      </Box>

      <Box mt={3} display="flex" justifyContent="space-between">
        <ThemeButton color="default" variant="outlined" onClick={onPrevious} disabled={saving}>
          {intl.formatMessage({ id: 'pages.adhesionOp.previous' })}
        </ThemeButton>
        <ThemeButton color="primary" onClick={handleSave} loading={saving}>
          {isLast
            ? intl.formatMessage({ id: 'pages.adhesionOp.saveAndFinish' })
            : intl.formatMessage({ id: 'pages.adhesionOp.saveAndContinue' })}
        </ThemeButton>
      </Box>
    </Box>
  )
}

export const AdhesionOp = () => {
  const intl = useIntl()
  const global = useSelector((state) => state.global)
  const combos = useCombos(['tipo_mercado_adhesiones', 'cod_zona_zeus'])

  const {
    company,
    formState,
    changeFormState,
    setFormStateSaved,
    getInstallerPrimaryCompany,
    updateInstallerPrimaryCompany,
    loadingCompany: isLoadingPrimary,
    error: contextError,
  } = useCompanyProfileContext()

  const inputProps = { formState, onChange: changeFormState, combos, intl }

  const [filiales, setFiliales] = useState([])
  const [isLoadingFiliales, setIsLoadingFiliales] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  const [completedSteps, setCompletedSteps] = useState(new Set())
  const [savingPrimary, setSavingPrimary] = useState(false)
  const [allDone, setAllDone] = useState(false)

  useEffect(() => {
    if (global.empresa_id) {
      getInstallerPrimaryCompany(global.empresa_id)
    }
  }, [global.empresa_id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!company.id) return
    const empresasFiliales = company.empresas_filiales || []
    if (empresasFiliales.length === 0) return

    setIsLoadingFiliales(true)
    Promise.all(empresasFiliales.map((f) => requests.getById('/empresa_ca', f.id)))
      .then((results) => {
        const data = results.map((r) => r.data)
        setFiliales(data)
        if (data[0]?.iban_op)
          changeFormState({ target: { name: 'iban_op', value: data[0].iban_op } })
        if (data[0]?.certificado_iban_op)
          changeFormState({
            target: { name: 'certificado_iban_op', value: data[0].certificado_iban_op },
          })
        setFormStateSaved(true)
        const completed = new Set()
        data.forEach((f, idx) => {
          if (f.responsable_legal && f.nif_responsable_legal) completed.add(idx + 1)
        })
        setCompletedSteps(completed)
      })
      .catch(() => feedback('error', 'Error al cargar las empresas filiales'))
      .finally(() => setIsLoadingFiliales(false))
  }, [company.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const getCcaaLabel = (ccaaKey) =>
    combos.cod_zona_zeus?.data?.find((z) => z.key === String(ccaaKey))?.value || ccaaKey || null

  const steps = [
    {
      label:
        company.razon_social ||
        company.nombre ||
        intl.formatMessage({ id: 'pages.adhesionOp.companyProfile' }),
      ccaa: null,
    },
    ...filiales.map((f) => ({
      label: f.nombre || f.razon_social,
      ccaa: getCcaaLabel(f.comunidad_autonoma),
    })),
  ]

  const hasRgpd = !!formState.formulario_rgpd

  const isPrimaryComplete = !!(
    formState.responsable_legal &&
    formState.nif_responsable_legal &&
    formState.iban_op &&
    hasRgpd
  )

  useEffect(() => {
    if (!company.id) return
    if (isPrimaryComplete) {
      setCompletedSteps((prev) => new Set([...prev, 0]))
    }
  }, [company.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const propagateToFiliales = async (snapshot) => {
    if (filiales.length === 0) return
    await Promise.all(
      filiales.map((filial) =>
        requests.putById(
          '/empresa_ca',
          filial.id,
          assembleInstallerSecondaryPutPayload({
            ...filial,
            responsable_legal: snapshot.responsable_legal || '',
            nif_responsable_legal: snapshot.nif_responsable_legal || '',
            check_resp_sec_info: true,
            iban_op: snapshot.iban_op,
            certificado_iban_op_nuevo: snapshot.certificado_iban_op_nuevo,
          })
        )
      )
    )
    if (snapshot.iban_op) changeFormState({ target: { name: 'iban_op', value: snapshot.iban_op } })
    if (snapshot.certificado_iban_op)
      changeFormState({
        target: { name: 'certificado_iban_op', value: snapshot.certificado_iban_op },
      })
    setFormStateSaved(true)
  }

  const handleSavePrimary = async () => {
    if (!formState.responsable_legal || !formState.nif_responsable_legal) {
      feedback('error', 'El Responsable Legal y su NIF son obligatorios')
      return
    }
    if (!hasRgpd) {
      feedback('error', RGPD_MISSING_MESSAGE)
      return
    }
    const snapshot = {
      responsable_legal: formState.responsable_legal,
      nif_responsable_legal: formState.nif_responsable_legal,
      iban_op: formState.iban_op,
      certificado_iban_op: formState.certificado_iban_op,
      certificado_iban_op_nuevo: formState.certificado_iban_op_nuevo,
    }
    const primaryCompanyHasChanges =
      formState.responsable_legal !== (company.responsable_legal || '') ||
      formState.nif_responsable_legal !== (company.nif_responsable_legal || '') ||
      !!formState.certificado_iban_op_nuevo ||
      !!formState.formulario_rgpd_nuevo
    setSavingPrimary(true)
    try {
      if (primaryCompanyHasChanges) {
        await updateInstallerPrimaryCompany()
      }
      await propagateToFiliales(snapshot)
      feedback('success', 'Empresa principal guardada correctamente')
      setCompletedSteps((prev) => new Set([...prev, 0]))
      if (filiales.length > 0) setActiveStep(1)
    } catch {
    } finally {
      setSavingPrimary(false)
    }
  }

  const handleFilialSaved = (stepIndex) => {
    setCompletedSteps((prev) => new Set([...prev, stepIndex]))
    const nextStep = stepIndex + 1
    if (nextStep >= steps.length) {
      setAllDone(true)
    } else {
      setActiveStep(nextStep)
    }
  }

  if (isLoadingPrimary || isLoadingFiliales) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={6}>
        <CircularProgress size={40} />
      </Box>
    )
  }

  if (allDone) {
    return (
      <Box
        bgcolor="common.white"
        borderRadius={4}
        display="flex"
        flexDirection="column"
        alignItems="center"
        p={6}
      >
        <CheckCircleOutlineIcon color="primary" style={{ fontSize: 72 }} />
        <Typography variant="h5" color="primary" gutterBottom style={{ marginTop: 16 }}>
          {intl.formatMessage({ id: 'pages.adhesionOp.adhesionCompleted' })}
        </Typography>
        <Typography variant="body1" color="textSecondary" align="center">
          {intl.formatMessage({ id: 'pages.adhesionOp.allDataSaved' })}
        </Typography>
        <Box mt={3}>
          <ThemeButton
            color="primary"
            onClick={() => {
              setAllDone(false)
              setActiveStep(0)
            }}
          >
            {intl.formatMessage({ id: 'pages.adhesionOp.returnToForm' })}
          </ThemeButton>
        </Box>
      </Box>
    )
  }

  return (
    <Box bgcolor="common.white" p={4} borderRadius={4}>
      <Typography variant="h5" color="primary" gutterBottom>
        {intl.formatMessage({ id: 'pages.adhesionOp.title' })}
      </Typography>

      {steps.length > 1 && (
        <Box mb={3}>
          <Stepper activeStep={activeStep} alternativeLabel nonLinear>
            {steps.map((step, idx) => (
              <Step key={step.label} completed={completedSteps.has(idx)}>
                <StepLabel style={{ cursor: 'pointer' }} onClick={() => setActiveStep(idx)}>
                  <span>
                    {step.label}
                    {step.ccaa && (
                      <Typography
                        variant="caption"
                        display="block"
                        align="center"
                        color="textSecondary"
                      >
                        ({step.ccaa})
                      </Typography>
                    )}
                  </span>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      )}

      {activeStep === 0 && (
        <Box mt={2}>
          {contextError?.message && (
            <Box mb={2}>
              <Alert severity="error">{contextError.message}</Alert>
            </Box>
          )}
          {isPrimaryComplete && (
            <Box mb={2}>
              <Alert severity="info">
                {intl.formatMessage({ id: 'pages.adhesionOp.companyDataAlreadyFilled' })}
              </Alert>
            </Box>
          )}
          <Typography variant="subtitle1" color="primary" gutterBottom>
            {intl.formatMessage({ id: 'pages.adhesionOp.primaryCompanyData' })}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextInput {...COMPANY_FORM_CONFIG(inputProps).RESPONSABLE_LEGAL} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextInput {...COMPANY_FORM_CONFIG(inputProps).NIF_RESPONSABLE_LEGAL} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextInput {...COMPANY_FORM_CONFIG(inputProps).IBAN_OP} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FileUploaderInput
                {...COMPANY_FORM_CONFIG(inputProps).CERTIFICADO_BANCARIO_IBAN_SWIFT}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box display="flex" flexDirection="column" gap={1}>
                <FileUploaderInput {...COMPANY_FORM_CONFIG(inputProps).FORMULARIO_RGPD} readOnly={true} />
                {!formState.formulario_rgpd && (
                  <Alert severity="error">
                    {intl.formatMessage({ id: 'pages.adhesionOp.rgpdMissing' })}
                  </Alert>
                )}

                {/* Enlace/Botón de redirección hacia el cuestionario RGPD en WordPress */}
                <Box mt={1}>
                  <a
                    href={`${REACT_APP_WP_CONTENT_CREATOR_URL}/cuestionario-privacidad-evaluacion-de-proveedores/?uuid=${company.uuid_rgpd || company.id || global.empresa_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#004a6b',
                      fontWeight: '600',
                      fontSize: '0.875rem',
                      textDecoration: 'underline',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    {intl.formatMessage({
                      id: formState.formulario_rgpd
                        ? 'pages.adhesionOp.rgpdUpdateLink'
                        : 'pages.adhesionOp.rgpdFillLink',
                    })}
                  </a>
                </Box>

              </Box>
            </Grid>
          </Grid>
          <Box mt={3} display="flex" justifyContent="flex-end">
            <ThemeButton color="primary" onClick={handleSavePrimary} loading={savingPrimary}>
              {filiales.length === 0
                ? intl.formatMessage({ id: 'pages.adhesionOp.save' })
                : intl.formatMessage({ id: 'pages.adhesionOp.saveAndContinue' })}
            </ThemeButton>
          </Box>
        </Box>
      )}

      {filiales.map((filial, idx) => {
        const stepIdx = idx + 1
        if (activeStep !== stepIdx) return null
        return (
          <FilialStep
            key={filial.id}
            filial={filial}
            combos={combos}
            isLast={stepIdx === steps.length - 1}
            onSaveAndContinue={() => handleFilialSaved(stepIdx)}
            onPrevious={() => setActiveStep(stepIdx - 1)}
            primaryHasRgpd={hasRgpd}
          />
        )
      })}
    </Box>
  )
}
